import React, { useState, useEffect } from 'react';
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiHome,
  FiFileText,
  FiClock,
  FiUser,
  FiSettings,
  FiLogOut,
  FiMenu,
  FiX,
  FiTrendingUp,
  FiCalendar,
  FiDollarSign,
  FiCreditCard,
  FiBell,
  FiSun,
  FiMoon,
  FiShield
} from 'react-icons/fi';
import {
  FaHome,
  FaFileInvoiceDollar,
  FaHistory,
  FaUserCircle,
  FaCog,
  FaSignOutAlt,
  FaBars,
  FaTimes,
  FaChartLine,
  FaGem,
  FaStar,
  FaBuilding
} from 'react-icons/fa';
import Swal from 'sweetalert2';
import logo from '../../assets/images/logo_new.jpeg';
import '../../styles/scrollbar.css';
import '../../styles/animations.css';
import { useDispatch } from 'react-redux';
import { setValue } from '../../features/UserInfo/userSlice';

const CustomerDashboardLayout = ({ isRefresh, setIsRefresh }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    return window.innerWidth >= 1024;
  });
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Check for saved theme preference
  useEffect(() => {
    const savedTheme = localStorage.getItem('customerTheme');
    if (savedTheme === 'dark') {
      setIsDarkMode(true);
      document.documentElement.classList.add('dark');
    }
  }, []);

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
    if (!isDarkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('customerTheme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('customerTheme', 'light');
    }
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const handleLogout = () => {
    localStorage.removeItem('auth_token');
    setIsRefresh?.("again");
    dispatch(setValue("Logout"));
    navigate('/login');
  };

  const confirmLogout = () => {
    Swal.fire({
      title: 'Are you sure?',
      text: 'You will be logged out of your account',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#6366f1',
      cancelButtonColor: '#ef4444',
      confirmButtonText: 'Yes, log out',
      cancelButtonText: 'Cancel',
      background: '#ffffff',
      customClass: {
        title: 'text-gray-800 font-semibold',
        content: 'text-gray-600',
        confirmButton: 'px-4 py-2 rounded-lg font-medium',
        cancelButton: 'px-4 py-2 rounded-lg font-medium'
      }
    }).then((result) => {
      if (result.isConfirmed) {
        handleLogout();
        if (window.innerWidth < 1024) {
          setIsSidebarOpen(false);
        }
      }
    });
  };

  const menuItems = [
    { path: '/customer/dashboard', icon: <FiHome className="w-5 h-5" />, label: 'Overview', description: 'Dashboard' },
    { path: '/customer/properties', icon: <FaBuilding className="w-5 h-5" />, label: 'My Properties', description: 'View properties' },
    { path: '/customer/payments', icon: <FiDollarSign className="w-5 h-5" />, label: 'EMI & Payments', description: 'Payment history' },
    { path: '/customer/profile', icon: <FiUser className="w-5 h-5" />, label: 'My Profile', description: 'Account settings' },
  ];

  const handleNavClick = () => {
    if (window.innerWidth < 1024) {
      setIsSidebarOpen(false);
    }
  };

  const getPageTitle = () => {
    const path = location.pathname.split('/').pop();
    const item = menuItems.find(item => item.path === location.pathname);
    return item?.label || path?.charAt(0).toUpperCase() + path?.slice(1) || 'Dashboard';
  };

  const navVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: (i) => ({
      opacity: 1,
      x: 0,
      transition: { delay: i * 0.05, duration: 0.3 }
    })
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-indigo-50/30 to-purple-50/30 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      {/* Sidebar Backdrop */}
      <AnimatePresence>
        {isSidebarOpen && window.innerWidth < 1024 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-30 lg:hidden"
            onClick={toggleSidebar}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <motion.aside
        initial={{ x: -320 }}
        animate={{ x: isSidebarOpen ? 0 : -320 }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        className={`fixed top-0 left-0 z-40 h-full w-80 bg-gradient-to-br from-indigo-900 via-purple-900 to-indigo-900 text-white shadow-2xl transform transition-transform duration-300 ease-in-out lg:translate-x-0 flex flex-col overflow-hidden`}
      >
        {/* Animated Background */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 -left-4 w-72 h-72 bg-white rounded-full mix-blend-multiply filter blur-xl animate-pulse"></div>
          <div className="absolute bottom-0 -right-4 w-72 h-72 bg-purple-400 rounded-full mix-blend-multiply filter blur-xl animate-pulse delay-1000"></div>
        </div>

        <div className="relative z-10 flex flex-col h-full">
          {/* Logo Section */}
          <div className="flex items-center justify-between h-20 px-6 border-b border-white/10">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-yellow-500 flex items-center justify-center shadow-lg">
                <FaGem className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold bg-gradient-to-r from-white to-purple-200 bg-clip-text text-transparent">
                  Dharaa Pariwar
                </h1>
                <p className="text-xs text-purple-300">Customer Portal</p>
              </div>
            </div>
            <button
              onClick={toggleSidebar}
              className="lg:hidden p-2 rounded-xl hover:bg-white/10 transition"
            >
              <FiX className="w-5 h-5 text-white" />
            </button>
          </div>

          {/* User Profile */}
          <div className="p-5 mx-4 mt-4 bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20">
            <div className="flex items-center gap-4">
              <div className="relative">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-amber-400 to-purple-500 flex items-center justify-center">
                  <FaUserCircle className="w-10 h-10 text-white" />
                </div>
                <div className="absolute bottom-0 right-0 w-3.5 h-3.5 bg-green-500 rounded-full border-2 border-indigo-900 animate-pulse"></div>
              </div>
              <div>
                <h3 className="font-bold text-white">Customer</h3>
                <p className="text-sm text-purple-300">Welcome back!</p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-6 overflow-y-auto custom-scrollbar">
            {menuItems.map((item, index) => (
              <motion.div
                key={item.path}
                custom={index}
                initial="hidden"
                animate="visible"
                variants={navVariants}
                className="mb-1"
              >
                <Link
                  to={item.path}
                  onClick={handleNavClick}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 group ${
                    location.pathname === item.path
                      ? 'bg-gradient-to-r from-amber-500/20 to-purple-500/20 text-white border border-white/10 shadow-lg'
                      : 'text-purple-200 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <div className={`p-1.5 rounded-lg transition-all duration-200 ${
                    location.pathname === item.path
                      ? 'bg-amber-500/30'
                      : 'group-hover:bg-white/10'
                  }`}>
                    {item.icon}
                  </div>
                  <div>
                    <span className="font-medium text-sm">{item.label}</span>
                    <p className="text-xs opacity-70">{item.description}</p>
                  </div>
                </Link>
              </motion.div>
            ))}
          </nav>

          {/* Sidebar Footer */}
          <div className="p-4 border-t border-white/10 mt-auto">
            <button
              onClick={confirmLogout}
              className="flex items-center w-full gap-3 px-4 py-3 text-purple-200 hover:bg-red-500/20 hover:text-red-400 rounded-xl transition-all duration-200 group"
            >
              <FiLogOut className="w-5 h-5 group-hover:rotate-12 transition" />
              <span className="font-medium">Logout</span>
            </button>
          </div>
        </div>
      </motion.aside>

      {/* Main Content */}
      <div className={`flex-1 transition-all duration-300 ${isSidebarOpen ? 'lg:ml-80' : ''}`}>
        {/* Header */}
        <header className="sticky top-0 z-20 bg-white/95 dark:bg-gray-800/95 backdrop-blur-sm shadow-sm border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between h-20 px-4 sm:px-6">
            <div className="flex items-center gap-3">
              <button
                onClick={toggleSidebar}
                className="lg:hidden p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition"
              >
                <FiMenu className="w-5 h-5 text-gray-600 dark:text-gray-300" />
              </button>
              <div>
                <h2 className="text-xl font-semibold text-gray-800 dark:text-white">
                  {getPageTitle()}
                </h2>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  Welcome to your dashboard
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* Theme Toggle */}
              <button
                onClick={toggleTheme}
                className="p-2 rounded-xl bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition"
              >
                {isDarkMode ? <FiSun className="w-4 h-4" /> : <FiMoon className="w-4 h-4" />}
              </button>
              {/* Date Display */}
              <div className="hidden sm:flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                <FiCalendar className="w-4 h-4" />
                {new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 relative">
          <div className="py-6 px-4 sm:px-6">
            <div className="max-w-7xl mx-auto">
              <AnimatePresence mode="wait">
                <motion.div
                  key={location.pathname}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.3 }}
                  className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 p-4 sm:p-6"
                >
                  <Outlet />
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </main>
      </div>

      {/* Custom Scrollbar Styles */}
      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.1);
          border-radius: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.3);
          border-radius: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 255, 255, 0.5);
        }
      `}</style>
    </div>
  );
};

export default CustomerDashboardLayout;